import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import { SPECIALS } from '../../../common/constants';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import { Tag } from '../../tagsearch/[tagIds]';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import useSpecialPage from '../../../hooks/useSpecialPage';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import SideBarStickyContent from '../../../components/SideBarStickyContent';

export const setting = {
  specialPageSlug: 'resident-handbook-campaign',
  specialPageShortName: SPECIALS.residentHandbookCampaign.name,
  specialPagePath: SPECIALS.residentHandbookCampaign.link,
  specialPageTitle: SPECIALS.residentHandbookCampaign.alt,
  sideBanner: () => (
    <SideBarStickyContent stickyHeight={45}>
      <Box _hover={{ opacity: '0.8' }}>
        <NextLink
          href="https://www.medicalview.co.jp/campaign/antaa2025resident/index.php?utm_source=antaa&utm_medium=banner&utm_campaign=202503resident"
          passHref
        >
          <Link
            target="_blank"
            onClick={() =>
              sendAnalyticsEvent(
                'special_contents',
                'click',
                'resident_handbook_campaign_side_banner',
              )
            }
          >
            <Image
              src="/images/special/resident_handbook_campaign_side_banner.png"
              alt="研修医応援おすすめ本"
              width={720}
              height="auto"
              style={{ objectFit: 'cover' }}
            />
          </Link>
        </NextLink>
      </Box>
    </SideBarStickyContent>
  ),
  listTitle: null, // 一覧タイトルをタグ配列から生成せず固定文字列にする場合はここに設定
};

const articleIds = SPECIALS.residentHandbookCampaign.articleIds ?? [];
const mainImagePath = SPECIALS.residentHandbookCampaign.mainImage ?? '';
const userAccessHistoryOperation = 'special_residHb'; // user_access_histories.operationカラムの値のため合計16文字以内

const description = (
  <>
    <Text fontSize="sm" fontWeight="bold" mb={2}>
      企画概要
    </Text>
    <Text fontSize="sm">メジカルビュー社 × Antaa共同企画</Text>
    <br />
    <Text fontSize="sm">
      「研修医が迷ったときに開く教科書をつくりませんか？」
    </Text>
    <br />
    <Text fontSize="sm">
      本企画は、初期臨床研修医が全国どこにいても、多くの医師の知識や経験から学べる環境が生まれることを目指しています。
    </Text>
    <br />
    <Text fontSize="sm">
      初期研修医は、初めての患者対応や知識、手技にどのように対処すべきか日々悩んでいます。また、施設や診療科によって「現場の常識」が異なることも、ローテート中の研修医にとって大きな負担となっています。働き方改革により、施設によっては十分な研修を行うことが困難な場合もあります。
    </Text>
    <br />
    <Text fontSize="sm">
      そこで、「自分が研修医時代に知っておきたかったこと」「これから研修を迎える先生方に伝えたいこと」「私の診療科ではこれだけは学んでほしいこと」など、先生方の貴重な知識や経験を、スライド投稿いただきました！
    </Text>
    <br />
    <Text fontSize="sm">
      ご投稿いただいた内容は、将来的な書籍化も視野に入れております。先生方が培ってこられた貴重なご経験を、本プロジェクトを通じて様々な形で次世代に届けることができましたら幸いです。{' '}
    </Text>
    <br />
    <br />
    <Text fontSize="sm">※エントリー終了のご案内※</Text>
    <Text fontSize="sm">本企画の新規エントリーは受付を終了しました。</Text>
    <Text fontSize="sm">ご投稿いただいた先生方、ありがとうございました。</Text>
  </>
);

// ここより下は特集ページごとの固有の内容は無し

type PageContext = {
  articles: ArticleHeader[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

const ArticleSection = ({
  articles,
  isMobile,
  specialPageShortName,
}: {
  articles: ArticleHeader[];
  isMobile: boolean;
  specialPageShortName: string;
}) => {
  return (
    <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
      {articles.length > 0 && (
        <Box pt={2} pb={2}>
          <Flex justifyContent="space-between" mb={1}>
            <Text fontWeight="bold" fontSize="md">
              #研修医の教科書 に関するスライド
            </Text>
          </Flex>
          <Flex direction={'column'}>
            <ArticlesGrid
              articles={articles}
              isMobile={isMobile}
              gaEventLabel={`${specialPageShortName}_tagsearch`}
              responsibleColumns={specialPageRegularColumns}
              onShowThumbnail={(articleId) => {
                sendAnalyticsEvent(
                  'slide_thumbnail',
                  'show',
                  `special_${specialPageShortName}#${articleId}`,
                );
              }}
              onClick={(articleId) => {
                sendAnalyticsEvent(
                  'slide_thumbnail',
                  'click',
                  `special_${specialPageShortName}#${articleId}`,
                );
              }}
            />
          </Flex>
        </Box>
      )}
    </Box>
  );
};

const ResidentHandbookCampaignPage: NextPage<PageContext> = ({
  articles,
  isMobile,
  isDemoAccount,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isPrivate,
}) => {
  const {
    specialPageTitle,
    specialPagePath,
    specialPageShortName,
    sideBanner,
    listTitle,
    specialPageSlug,
  } = setting;

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          {/* main start */}
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box pos="relative" mb={2}>
                <NextImage
                  src={mainImagePath}
                  alt={specialPageTitle}
                  width={1300}
                  height={720}
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>{description}</Box>
              </Box>
            </Box>
            {/* main end */}
            {/* 3rd start */}
            <ArticleSection
              articles={articles}
              isMobile={isMobile}
              specialPageShortName={SPECIALS.residentHandbookCampaign.name}
            />
          </GridItem>
          {/* 3rd end */}
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {sideBanner()}
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default ResidentHandbookCampaignPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { specialPageSlug } = setting;

    const { isPrivate } = await getSpecialArticleProps(specialPageSlug, userId);

    const articleHeaders = await prisma.article_header.findMany({
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: articleIds,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
      orderBy: {
        create_date: 'desc',
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v: any) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v: any) => !!v);
    // アカウント統合済みのユーザのプロフィールのみ返却される
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    const articles: ArticleHeader[] = articleHeaders.reduce(
      (articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: author.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds: author.update_date.getTime(),
            profilePhotoS3Key: author.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      },
      [],
    );

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        articles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
